import { objectSpread2 as _objectSpread2, slicedToArray as _slicedToArray, asyncToGenerator as _asyncToGenerator } from '../../_virtual/_rollupPluginBabelHelpers.js';
import { useRef, useState, useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { abortableApi } from '../../modules/abort/actions.js';

var defaultOptions = {
  lazy: true,
  persistResponse: false,
  actionSlowDelay: 200
};
/**
 * A hook to trigger an existing API action
 * @param {function} action API action creator
 * @param {object} options Additional options for the API request
 * @param {boolean} options.lazy Optional, whether the API request should be fired on demand or immediately, default true
 * @param {number} options.actionSlowDelay Optional, time delay before the request indicates it is taking a long time, default 200ms
 * @param {*} options.actionParams Optional, additional parameters to supply to the request action
 * @returns {[]} An array in the format: [requestState, sendRequest, abortRequest]
 * @returns {object} `requestState` The current state of the request: { success, error, response, busy, slow, aborted, sentAt, receivedAt, abortedAt }
 * @returns {function} `sendRequest` A function to fire the request
 * @returns {function} `abortRequest` A function to abort the request
 */

var useApiAction = function useApiAction(action) {
  var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

  var _options = _objectSpread2(_objectSpread2({}, defaultOptions), options);

  var isMounted = useRef(true);
  var loadingKey = useRef(0);
  var loadingAbort = useRef(undefined);

  var _useState = useState({
    busy: false,
    aborted: false
  }),
      _useState2 = _slicedToArray(_useState, 2),
      requestState = _useState2[0],
      setRequestState = _useState2[1];

  var dispatch = useDispatch();
  var sendRequest = useCallback( /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/regeneratorRuntime.mark(function _callee() {
    var actionParams,
        requestKey,
        requestAction,
        abort,
        slowTimeout,
        apiResult,
        aborted,
        success,
        response,
        error,
        _args = arguments;
    return regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            actionParams = _args.length > 0 && _args[0] !== undefined ? _args[0] : _options.actionParams;
            requestKey = loadingKey.current = Date.now();
            requestAction = abortableApi(action(actionParams));
            abort = requestAction.abort;
            loadingAbort.current = abort;
            setRequestState(function (state) {
              return {
                response: _options.persistResponse ? state.response : undefined,
                busy: true,
                aborted: abort.aborted,
                sentAt: Date.now()
              };
            });
            slowTimeout = setTimeout(function () {
              if (!isMounted.current) return;
              setRequestState(function (state) {
                return _objectSpread2(_objectSpread2({}, state), {}, {
                  slow: true
                });
              });
            }, _options.actionSlowDelay);
            _context.next = 9;
            return dispatch(requestAction);

          case 9:
            apiResult = _context.sent;
            aborted = abort.aborted;
            clearTimeout(slowTimeout); // prevent state changes after unmounting

            if (isMounted.current) {
              _context.next = 14;
              break;
            }

            return _context.abrupt("return", apiResult);

          case 14:
            if (!(requestKey !== loadingKey.current)) {
              _context.next = 16;
              break;
            }

            return _context.abrupt("return", apiResult);

          case 16:
            success = apiResult.success, response = apiResult.response, error = apiResult.error;
            setRequestState(function (state) {
              return _objectSpread2(_objectSpread2({}, state), {}, {
                success: success,
                response: response,
                error: error,
                busy: false,
                slow: false,
                aborted: aborted,
                receivedAt: aborted ? undefined : Date.now(),
                abortedAt: aborted ? Date.now() : undefined
              });
            });
            return _context.abrupt("return", apiResult);

          case 19:
          case "end":
            return _context.stop();
        }
      }
    }, _callee);
  })), [action, _options.actionParams, _options.persistResponse, _options.actionSlowDelay, dispatch, abortableApi, setRequestState]);
  var abortRequest = useCallback(function () {
    var _loadingAbort$current;

    (_loadingAbort$current = loadingAbort.current) === null || _loadingAbort$current === void 0 ? void 0 : _loadingAbort$current.abort();
  }, []);
  useEffect(function () {
    if (!_options.lazy) {
      sendRequest();
    }

    return function () {
      isMounted.current = false;
    };
  }, []);
  return [requestState, sendRequest, abortRequest];
};

var useApiAction$1 = useApiAction;

export { useApiAction$1 as default };
